class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedProducts = null;
    this.isLoadingProducts = false;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;

    this.setupEventListeners();
    this.preloadProducts(); // Start loading ALL products in background
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  // Load ALL products from the store using pagination
  async preloadProducts() {
    if (this.cachedProducts) {
      return this.cachedProducts;
    }

    if (this.isLoadingProducts) {
      // Already loading, wait for it
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (!this.isLoadingProducts) {
            clearInterval(checkInterval);
            resolve(this.cachedProducts || []);
          }
        }, 100);
      });
    }

    this.isLoadingProducts = true;
    console.log('📦 Loading ALL products from store...');

    try {
      let allProducts = [];
      let page = 1;
      let hasMore = true;
      const maxPages = 10; // Load up to 2500 products (10 pages × 250)

      while (hasMore && page <= maxPages) {
        console.log(`📄 Loading page ${page}...`);
        
        // Try multiple endpoints
        const endpoints = [
          `/products.json?limit=250&page=${page}`,
          `/collections/all/products.json?limit=250&page=${page}`
        ];

        let pageProducts = null;

        for (const endpoint of endpoints) {
          try {
            const response = await fetch(endpoint);
            if (response.ok) {
              const data = await response.json();
              pageProducts = data.products || [];
              console.log(`✅ Page ${page}: Loaded ${pageProducts.length} products from ${endpoint}`);
              break;
            }
          } catch (err) {
            console.log(`⏭️ Skipping ${endpoint}`);
          }
        }

        if (!pageProducts || pageProducts.length === 0) {
          console.log(`🏁 No more products on page ${page}`);
          hasMore = false;
        } else {
          allProducts = [...allProducts, ...pageProducts];
          
          // If we got less than 250, we've reached the end
          if (pageProducts.length < 250) {
            console.log(`🏁 Reached last page (got ${pageProducts.length} products)`);
            hasMore = false;
          } else {
            page++;
          }
        }
      }

      console.log(`✅ Total products loaded: ${allProducts.length}`);

      // Count products with SKUs
      const productsWithSKUs = allProducts.filter(p => 
        p.variants && p.variants.some(v => v.sku)
      );
      console.log(`📝 Products with SKUs: ${productsWithSKUs.length}`);

      this.cachedProducts = allProducts;
      this.isLoadingProducts = false;
      return allProducts;

    } catch (error) {
      console.error('❌ Error loading products:', error);
      this.isLoadingProducts = false;
      return [];
    }
  }

  async searchProductsBySKU(searchTerm) {
    try {
      let products = this.cachedProducts;
      
      if (!products) {
        console.log('⏳ Waiting for products to load...');
        products = await this.preloadProducts();
      }

      if (!products || products.length === 0) {
        console.warn('⚠️ No products available for SKU search');
        return [];
      }

      const searchLower = searchTerm.toLowerCase();
      
      const matchingProducts = products.filter(product => {
        if (!product.variants || product.variants.length === 0) {
          return false;
        }
        
        return product.variants.some(variant => {
          if (!variant.sku) return false;
          return variant.sku.toLowerCase().includes(searchLower);
        });
      });

      console.log(`🎯 Found ${matchingProducts.length} products with SKU containing "${searchTerm}"`);

      return matchingProducts.slice(0, 5).map(product => ({
        id: product.id,
        handle: product.handle,
        url: `/products/${product.handle}`,
        title: product.title,
        vendor: product.vendor,
        image: product.images && product.images[0] ? product.images[0].src || product.images[0] : '',
        price: product.variants && product.variants[0] ? product.variants[0].price : 0,
        compareAtPrice: product.variants && product.variants[0] ? product.variants[0].compare_at_price : null,
        available: product.variants && product.variants.some(v => v.available),
        variants: product.variants,
        matchedBySKU: true
      }));
      
    } catch (error) {
      console.error('❌ SKU search error:', error);
      return [];
    }
  }

  async searchProductsByTag(searchTerm) {
    try {
      const response = await fetch(`/search?q=tag:${encodeURIComponent(searchTerm)}*&type=product`);
      if (!response.ok) return [];
      
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      
      const productLinks = doc.querySelectorAll('a[href*="/products/"]');
      const handles = new Set();
      
      productLinks.forEach(link => {
        const match = link.href.match(/\/products\/([^/?#]+)/);
        if (match && match[1]) {
          handles.add(match[1]);
        }
      });
      
      if (handles.size === 0) return [];
      
      const productPromises = Array.from(handles).slice(0, 5).map(handle =>
        fetch(`/products/${handle}.js`)
          .then(res => res.ok ? res.json() : null)
          .catch(() => null)
      );
      
      const products = await Promise.all(productPromises);
      
      return products
        .filter(p => p !== null)
        .map(product => ({
          id: product.id,
          handle: product.handle,
          url: `/products/${product.handle}`,
          title: product.title,
          vendor: product.vendor,
          image: product.featured_image || (product.images && product.images[0]) || '',
          price: product.price,
          compareAtPrice: product.compare_at_price,
          available: product.available,
          variants: product.variants
        }));
      
    } catch (error) {
      console.log('Tag search error:', error);
      return [];
    }
  }

  async getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey =
      searchTerm.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = this.dataset.limit;
    const limitScope = this.dataset.limitScope;
    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);

    try {
      const response = await fetch(
        `${routes.predictive_search_url}?q=${query}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&section_id=predictive-search`
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const text = await response.text();
      const resultsDoc = new DOMParser().parseFromString(text, "text/html");
      const resultsSection = resultsDoc.querySelector("#shopify-section-predictive-search");

      const [skuProducts, tagProducts] = await Promise.all([
        this.searchProductsBySKU(searchTerm),
        this.searchProductsByTag(searchTerm)
      ]);
      
      const additionalProducts = [...skuProducts, ...tagProducts];
      const uniqueProducts = [];
      const seenIds = new Set();
      
      additionalProducts.forEach(product => {
        if (!seenIds.has(product.id)) {
          seenIds.add(product.id);
          uniqueProducts.push(product);
        }
      });
      
      if (uniqueProducts.length > 0) {
        const productList = resultsSection.querySelector('#predictive-search-results-item-product-list');
        
        if (productList) {
          const existingUrls = new Set(
            Array.from(productList.querySelectorAll('a')).map(a => new URL(a.href).pathname)
          );

          uniqueProducts.forEach((product, index) => {
            if (!existingUrls.has(product.url)) {
              const li = this.createProductListItem(product, productList.children.length + index + 1);
              productList.appendChild(li);
            }
          });
        } else {
          const productsSection = resultsSection.querySelector('.predictive-search-results-group:last-child');
          if (productsSection && uniqueProducts.length > 0) {
            const productSectionHtml = `
              <div class="predictive-search-results-group-section">
                <h3 id="predictive-search-products" class="predictive-search-heading predictive-search-heading-products h5">
                  Products
                </h3>
                <ul id="predictive-search-results-item-product-list" class="predictive-search-results-item-list">
                  ${uniqueProducts.map((product, index) => this.createProductListItem(product, index + 1).outerHTML).join('')}
                </ul>
              </div>
            `;
            productsSection.insertAdjacentHTML('afterbegin', productSectionHtml);
          }
        }
      }

      const resultsMarkup = resultsSection.innerHTML;
      this.cachedResults[queryKey] = resultsMarkup;
      this.renderSearchResults(resultsMarkup);
    } catch (error) {
      console.error('Search error:', error);
      this.removeAttribute("loading");
      this.close();
    }
  }

  createProductListItem(product, index) {
    const li = document.createElement('li');
    li.id = `predictive-search-option-product-${index}`;
    li.className = 'predictive-search-list-item predictive-search-list-item-product';
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    
    let imageHTML = '';
    if (product.image) {
      let imageUrl = typeof product.image === 'string' ? product.image : product.image.src || product.image;
      
      if (typeof imageUrl === 'string' && imageUrl.includes('cdn.shopify.com')) {
        imageUrl = imageUrl.replace(/(_\d+x\d*|_compact|_grande|_large|_medium|_small|_thumb)\.(jpg|jpeg|png|gif|webp)/i, '_108x.$2');
        if (!imageUrl.includes('_108x.')) {
          imageUrl = imageUrl.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_108x.$1');
        }
      }
      
      imageHTML = `<img src="${imageUrl}" class="predictive-search-item-image" width="108" loading="lazy" alt="${this.escapeHtml(product.title)}">`;
    }
    
    let vendorHTML = '';
    if (product.vendor) {
      vendorHTML = `<span class="visually-hidden">Verkäufer</span>
                    <div class="predictive-search-item-vendor">${this.escapeHtml(product.vendor)}</div>`;
    }
    
    li.innerHTML = `
      <a href="${product.url}" class="predictive-search-item predictive-search-item-link" tabindex="-1">
        <div class="predictive-search-item-image-wrap color-background-2">${imageHTML}</div>
        <div class="predictive-search-item-content">
          ${vendorHTML}
          <h3 class="predictive-search-item-heading typography-body">
            ${this.escapeHtml(product.title)}
          </h3>
        </div>
      </a>
    `;
    
    return li;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    const top = this.querySelector(".predictive-search").getBoundingClientRect().top;
    this.predictiveSearchResults.style.maxHeight = `calc(100dvh - ${top}px - 30px)`;

    if (!this.isOpen) {
      publish(PUB_SUB_EVENTS.searchResultsOpen);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      publish(PUB_SUB_EVENTS.searchResultsClose);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);